import React, { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { sanitizeId } from "@/components/utils/id";
import PersonAvatar from "@/components/PersonAvatar";
import { personaFitBand, PERSONA_FIT_LABELS } from '@/lib/personaFitLabel';
import { useTranslation } from '@/components/utils/translations';

// [2026-10-29] i18n: display-only keys for the bucket headings. The English bucket names stay
// the grouping keys (ARCH_ORDER / ARCH_COLORS / grouped) and are what English renders.
const ARCH_LABEL_KEYS = {
  'Executive Buyer': 'acctArchExecutiveBuyer',
  'Champion': 'champion',
  'Influencer': 'influencer',
  'Evaluator': 'acctArchEvaluator',
  'End User': 'endUser',
};

// 1) Canonical buckets in display order
const ARCH_ORDER = ["Executive Buyer", "Champion", "Influencer", "Evaluator", "End User"];

const ARCH_COLORS = {
  "Executive Buyer": "bg-[#0B0F1A]",
  "Champion": "bg-[#1C485C]",
  "Influencer": "bg-[#3D8C91]",
  "Evaluator": "bg-[#89CDD4]",
  "End User": "bg-[#D8FAF8]",
};

// 2) Mapping dictionary (lowercased keys) – maps archetype_name to display bucket
const ARCH_MAP = {
  // Executive Buyer
  "executive buyer": "Executive Buyer",
  "economic buyer": "Executive Buyer",
  "economic buyer - executive decision maker": "Executive Buyer",
  "economic buyer - technology executive decision maker": "Executive Buyer",
  "executive decision maker (economic buyer)": "Executive Buyer",
  "technology economic buyer": "Executive Buyer",
  "economic buyer (executive decision maker)": "Executive Buyer",
  "economic buyer (technology executive decision maker)": "Executive Buyer",
  "executive (needs procurement evidence)": "Executive Buyer",
  "technology executive": "Executive Buyer",

  // Champion
  "champion": "Champion",
  "champion - domain strategy owner": "Champion",
  "champion - technology champion": "Champion",
  "champion (domain strategy owner)": "Champion",
  "technology champion": "Champion",
  "champion (technology champion)": "Champion",

  // Influencer
  "technology influencer": "Influencer",
  "influencer": "Influencer",
  "influencer - decision influencer": "Influencer",
  "influencer - executive (needs procurement evidence)": "Influencer",
  "influencer - technology executive": "Influencer",
  "technology executive (needs procurement evidence)": "Influencer",
  "senior decision maker": "Influencer",
  "student influencer": "Influencer",
  "influencer (decision influencer)": "Influencer",
  "decision influencer": "Influencer",

  // Evaluator
  "evaluator": "Evaluator",
  "evaluator - technical evaluator": "Evaluator",
  "technology evaluator": "Evaluator",
  "evaluator (technical evaluator)": "Evaluator",
  "technical evaluator": "Evaluator",

  // End User – only true end users and operational stakeholders
  "end user / other": "End User",
  "end user/other": "End User",
  "end user": "End User",
  "operational stakeholder": "End User",
};

// 3) Priority when multiple labels exist
const PRIORITY = ["Executive Buyer", "Champion", "Influencer", "Evaluator", "End User"];

function normalizeArchetype(raw) {
  if (!raw) return "End User";
  const items = Array.isArray(raw) ? raw : [raw];
  const mapped = items
    .map(v => String(v).trim().toLowerCase())
    .map(v => ARCH_MAP[v])
    .filter(Boolean);

  if (mapped.length === 0) return "End User";
  for (const bucket of PRIORITY) {
    if (mapped.includes(bucket)) return bucket;
  }
  return "End User";
}

// Fit badge style (reuse existing thresholds)
const getFitBadge = (score) => {
  if (score === null || score === undefined) return { label: "—", cls: "bg-gray-100 text-gray-700" };
  const band = personaFitBand(score) ?? 'passive';
  return { label: PERSONA_FIT_LABELS[band], cls: PERSONA_BADGE_CLASSES[band] };
};
const PERSONA_BADGE_CLASSES = {
  strategic: "bg-green-100 text-green-800",
  supportive: "bg-blue-100 text-blue-800",
  passive: "bg-gray-100 text-gray-800",
  lowFit: "bg-red-100 text-red-800",
};

export default function ArchetypeGrid({ personas, accountId, emptyCta = null }) {
  const { t } = useTranslation();
  const grouped = useMemo(() => {
    const base = {
      "Executive Buyer": [],
      "Champion": [],
      "Influencer": [],
      "Evaluator": [],
      "End User": [],
    };
    
    console.log('🔍 ArchetypeGrid: Processing personas:', personas?.length || 0);
    
    const unmappedArchetypes = new Set();
    
    for (const p of (personas || [])) {
      // Log each person's archetype data
      console.log('👤 Person archetype data:', {
        name: `${p.first_name} ${p.last_name}`,
        raw_archetype: p.archetype_name,
        has_tenant_overrides: !!p.tenant_overrides,
        tenant_overrides: p.tenant_overrides,
        person_id: p.id,
        account_id: p.account_id,
        account_name: p.account_name
      });

      const bucket = normalizeArchetype(p.archetype_name);
      
      // Debug logging for each persona
      if (p.archetype_name) {
        const rawValue = Array.isArray(p.archetype_name) ? p.archetype_name : [p.archetype_name];
        const lowercased = rawValue.map(v => String(v).trim().toLowerCase());
        
        console.log('📊 Persona archetype mapping:', {
          name: `${p.first_name} ${p.last_name}`,
          raw_archetype: p.archetype_name,
          lowercased: lowercased,
          mapped_to: bucket,
          in_map: lowercased.map(v => ARCH_MAP[v] || 'NOT FOUND')
        });
        
        // Track unmapped values
        lowercased.forEach(v => {
          if (!ARCH_MAP[v]) {
            unmappedArchetypes.add(v);
          }
        });
      }
      
      base[bucket].push(p);
    }
    
    // Log any unmapped archetype values
    if (unmappedArchetypes.size > 0) {
      console.warn('⚠️ Unmapped archetype values found:', Array.from(unmappedArchetypes));
    }

    console.log('📊 Final grouping:', Object.entries(base).map(([key, value]) => ({ 
      archetype: key, 
      count: value.length,
      people: value.map(p => `${p.first_name} ${p.last_name}`)
    })));
    
    return base;
  }, [personas]);

  // Check if all buckets are empty
  const totalPersonas = personas?.length || 0;
  if (totalPersonas === 0) {
    return (
      <div className="text-center py-8 text-[#5C6B7A] space-y-3">
        <p className="text-sm">No personas found for this account.</p>
        {emptyCta || null}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {ARCH_ORDER.map((bucket) => {
        const list = grouped[bucket] || [];
        const isBusy = list.length >= 4; // Consider busy when 4+ personas

        return (
          <section key={bucket} className="bg-white rounded-2xl border shadow-sm">
            <header className="flex items-center justify-between p-3 md:p-4">
              <div className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${ARCH_COLORS[bucket]}`} />
                <h3 className="text-[#0A1A2F] font-semibold text-sm md:text-base">{ARCH_LABEL_KEYS[bucket] ? t(ARCH_LABEL_KEYS[bucket]) : bucket}</h3>
              </div>
              <span className="text-xs text-[#5C6B7A]">{list.length}</span>
            </header>

            {list.length === 0 ? (
              <div className="px-3 md:px-4 pb-3 md:pb-4 text-sm text-[#5C6B7A]">{t('acctNoContactsYet')}</div>
            ) : (
              <div className={`p-3 md:p-4 pt-0 ${
                list.length === 1 ? 'grid grid-cols-1' :
                list.length === 2 ? 'grid grid-cols-1 md:grid-cols-2 gap-3' :
                list.length === 3 ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3' :
                'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-2'
              }`}>
                {list.map((p) => {
                  const name = p.full_name || [p.first_name, p.last_name].filter(Boolean).join(" ") || "Unknown";
                  const role = p.role_title || "—";
                  const dept = p.department || "";
                  const badge = getFitBadge(p.persona_fit_score);
                  const personIdForLink = sanitizeId(p.person_id || p.id);
                  if (!personIdForLink) return null;
                  const accountIdForLink = sanitizeId(p.account_id || accountId);

                  return (
                    <Link
                      key={p.id || p.person_id}
                      to={createPageUrl(
                        `PersonProfile?id=${encodeURIComponent(personIdForLink)}${
                          accountIdForLink ? `&accountId=${encodeURIComponent(accountIdForLink)}` : ''
                        }`
                      )}
                      className="group rounded-xl border bg-white p-3 flex items-center justify-between hover:shadow-md hover:border-[#00C2C2]/30 transition max-w-md"
                    >
                      <div className="min-w-0 flex-1 flex items-center gap-2">
                        <PersonAvatar
                          personId={p.person_id || p.id}
                          hostedPhotoUrl={p.hosted_photo_url}
                          linkedinPhotoUrl={p.profile_photo_url}
                          firstName={p.first_name}
                          lastName={p.last_name}
                          size={isBusy ? "sm" : "md"}
                        />
                        <div className="min-w-0 flex-1">
                          <div className={`font-semibold text-[#0A1A2F] truncate ${isBusy ? 'text-xs' : 'text-sm'}`}>{name}</div>
                          <div className={`text-[#5C6B7A] truncate ${isBusy ? 'text-[10px]' : 'text-xs md:text-sm'}`}>{role}</div>
                          {dept && (
                            <Badge className={`mt-1 bg-purple-100 text-purple-800 border-purple-200 ${isBusy ? 'text-[10px] px-1.5 py-0' : 'text-xs px-2 py-0.5'}`}>
                              {dept}
                            </Badge>
                          )}
                        </div>
                      </div>

                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="ml-2 shrink-0 cursor-help">
                              <Badge className={`${badge.cls} ${isBusy ? 'text-[10px] px-1.5 py-0.5' : 'text-xs'}`}>
                                {badge.label}
                                {p.persona_fit_score !== null && p.persona_fit_score !== undefined && ` (${p.persona_fit_score})`}
                              </Badge>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent 
                            side="top"
                            align="center"
                            className="w-[90vw] max-w-[500px] md:w-[500px] bg-[#0A1A2F] text-white border-0 shadow-lg p-4"
                          >
                            <p className="text-sm font-semibold mb-2 text-[#00C2C2]">Persona Fit Score</p>
                            {p.persona_score_rationale ? (
                              <p className="text-sm text-white leading-relaxed">{p.persona_score_rationale}</p>
                            ) : (
                              <p className="text-sm text-gray-300 leading-relaxed">No rationale available for this persona.</p>
                            )}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </Link>
                  );
                })}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}